import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Emplacement, ObjetDTO, PersonnageDTO, Stat } from '@whatremains/shared'
import { empiler, NOM_EMPLACEMENT, NOM_FAMILLE, NOM_PALIER } from '@whatremains/shared'
import { api, ErreurApi } from '../api'
import { ABREGES, Ecart, poids } from '../composants/Comparaison'
import { empechement, FicheObjet } from '../composants/FicheObjet'
import { IconeObjet } from '../composants/IconeObjet'

/**
 * L'inventaire.
 *
 * `08-INTERFACE.md` §4 : il ne se gère pas, il se trie.
 *
 *   - **La comparaison est automatique** : chaque pièce rangée affiche l'écart
 *     avec ce qui est porté à son emplacement, toujours, sans rien demander.
 *   - **Un clic pour agir.** Pas de menu, pas de fenêtre intermédiaire.
 *   - **Aucun glisser-déposer**, aucune grille à réorganiser, aucune limite de
 *     place arbitraire.
 */

const ORDRE: Emplacement[] = [
  'tete', 'cou', 'torse', 'dos', 'mains', 'taille',
  'jambes', 'pieds', 'arme_principale', 'arme_secondaire', 'anneau_1', 'anneau_2',
]


type Tri = 'emplacement' | 'palier' | 'valeur'


export function Inventaire({
  id,
  surChangement,
}: {
  id: string
  surChangement: () => void
}) {
  const [p, setP] = useState<PersonnageDTO | null>(null)
  const [objets, setObjets] = useState<ObjetDTO[]>([])
  const [tri, setTri] = useState<Tri>('emplacement')
  const [mieuxSeulement, setMieuxSeulement] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  /*
   * La piece ouverte. Une seule a la fois, et le clic sur la ligne l'ouvre.
   *
   * Enzo : « quand on clique sur les items qu'on a, il ne se passe rien ». Le
   * survol ne suffit pas — il n'existe pas sur telephone — donc c'est le clic
   * qui porte l'information, et il fonctionne partout.
   */
  const [ouvert, setOuvert] = useState<string | null>(null)

  const recharger = useCallback(async () => {
    const [perso, inv] = await Promise.all([api.personnage(id), api.inventaire(id)])
    setP(perso)
    setObjets(inv)
  }, [id])

  useEffect(() => { void recharger() }, [recharger])

  const portes = useMemo(
    () => new Map(objets.filter((o) => o.porte).map((o) => [o.emplacement as Emplacement, o])),
    [objets],
  )

  const ranges = useMemo(() => {
    let liste = objets.filter((o) => !o.porte)
    if (mieuxSeulement) {
      liste = liste.filter((o) => poids(o) > poids(portes.get(o.emplacementCible) ?? o) || !portes.get(o.emplacementCible))
    }
    const rang = (o: ObjetDTO) => ORDRE.indexOf(o.emplacementCible)
    if (tri === 'emplacement') return [...liste].sort((a, b) => rang(a) - rang(b) || a.nom.localeCompare(b.nom))
    if (tri === 'valeur') return [...liste].sort((a, b) => b.valeurMarchande - a.valeurMarchande)
    return [...liste].sort((a, b) => poids(b) - poids(a))
  }, [objets, tri, mieuxSeulement, portes])

  if (!p) return <div className="cadre"><div className="entete"><p className="tenu">…</p></div></div>

  async function agir(action: () => Promise<unknown>) {
    setErreur(null)
    try {
      await action()
      await recharger()
      surChangement()
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : "Quelque chose n'a pas abouti.")
    }
  }

  return (
    <div className="cadre">
      <div className="entete">
        <div className="surtitre">
          {portes.size} portés · {objets.length - portes.size} dans les sacs
        </div>
        <h1>Sacs</h1>
        {erreur && <p className="erreur">{erreur}</p>}
      </div>

      <section>
        <div className="surtitre">Porté</div>
        <div className="paires">
          {ORDRE.map((e) => {
            const o = portes.get(e)
            return (
              <div key={e}>
                <div className="paire">
                  <b>{NOM_EMPLACEMENT[e]}</b>
                  <span>
                    {o ? (
                      <>
                        {/* Le nom ouvre la fiche. C'est le seul geste a apprendre. */}
                        <button
                          className="nom-objet"
                          onClick={() => setOuvert(ouvert === o.id ? null : o.id)}
                        >
                          <IconeObjet objet={o} taille={22} />
                          <span className={`palier-${o.palier}`}>{o.nom}</span>
                        </button>
                        <span className="tenu" style={{ marginLeft: 12, fontFamily: 'var(--mono)', fontSize: 10.5 }}>
                          {(Object.keys(o.bonus) as Stat[])
                            .map((s) => `${ABREGES[s]} +${o.bonus[s]}`)
                            .join('  ')}
                        </span>
                        <button
                          className="discret"
                          style={{ marginLeft: 16 }}
                          onClick={() => void agir(() => api.ranger(id, o.id))}
                        >
                          retirer
                        </button>
                      </>
                    ) : (
                      <span className="emplacement-vide">vide</span>
                    )}
                  </span>
                </div>
                {o && ouvert === o.id && (
                  <FicheObjet
                    o={o}
                    classe={p.classe}
                    niveau={p.niveau}
                    surRanger={() => { setOuvert(null); void agir(() => api.ranger(id, o.id)) }}
                    surFermer={() => setOuvert(null)}
                  />
                )}
              </div>
            )
          })}
        </div>
      </section>

      <section>
        <div className="surtitre">Dans les sacs</div>

        <p style={{ display: 'flex', gap: 22, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
          {(['emplacement', 'palier', 'valeur'] as Tri[]).map((t) => (
            <button
              key={t}
              className="discret"
              style={{ color: tri === t ? 'var(--r7)' : undefined }}
              onClick={() => setTri(t)}
            >
              par {t}
            </button>
          ))}
          <button
            className="discret"
            style={{ color: mieuxSeulement ? 'var(--r7)' : undefined }}
            onClick={() => setMieuxSeulement(!mieuxSeulement)}
          >
            mieux que ce que je porte
          </button>
        </p>

        {ranges.length === 0 ? (
          <p className="tenu">Rien.</p>
        ) : (
          <div className="paires">
            {/*
              Les sacs s'empilent.

              Enzo : « il faut que les items soient stackables quand ce sont les
              mêmes items. Parce que là j'ai plein de ferraille, de pierre de
              taille et tout, et elles sont uniques, pas stackées. » Il en avait
              une ligne chacune.

              Le regroupement se fait à l'AFFICHAGE, avec la règle partagée que
              le serveur applique pour la vente : deux calculs séparés
              finiraient par annoncer une quantité et en vendre une autre. Rien
              ne fusionne en base, parce qu'une ligne porte son cran
              d'amélioration : deux brigandines dont l'une est montée d'un cran
              ne sont pas la même chose et ne s'additionnent pas.
            */}
            {empiler(ranges).map((lot) => {
              const o = lot.tete
              /*
               * Ce qui empeche de la porter, calcule AVANT d'afficher le bouton.
               *
               * Enzo, sur sa Canne a chainer : « je ne sais meme pas ou ca se
               * met, et quand je clique il ne se passe rien ». C'est un baton,
               * un rogue ne tient pas de baton, et le bouton appelait quand meme
               * le serveur pour se faire refuser en silence.
               */
              const bloque = empechement(o, p.classe, p.niveau)
              return (
                <div key={lot.cle}>
                  {/*
                    La grille en CLASSE, pas en style inline.

                    Inline, elle battait la requete media de 640 px : les trois
                    colonnes restaient serrees sur telephone au lieu de passer
                    en pile, et la colonne du milieu vient de gagner le palier
                    et la famille.
                  */}
                  <div className="paire paire-sac">
                    <b>{NOM_EMPLACEMENT[o.emplacementCible]}</b>
                    <span>
                      <button
                        className="nom-objet"
                        onClick={() => setOuvert(ouvert === o.id ? null : o.id)}
                      >
                        <IconeObjet objet={o} taille={22} />
                        <span className={`palier-${o.palier}`}>{o.nom}</span>
                        {lot.quantite > 1 && <span className="pile">×{lot.quantite}</span>}
                      </button>
                      {/*
                        Le palier en toutes lettres et la famille, sur la ligne.
                        La couleur du contour disait deja le palier, a qui
                        connait le code des huit teintes — c'est-a-dire a
                        personne, la premiere fois.
                      */}
                      <span className="objet-type">
                        {NOM_PALIER[o.palier]}
                        {o.categorie === 'equipement' && ` · ${NOM_FAMILLE[o.famille]}`}
                      </span>
                      <span style={{ marginLeft: 14 }}>
                        <Ecart range={o} porte={portes.get(o.emplacementCible)} />
                      </span>
                    </span>
                    <span style={{ whiteSpace: 'nowrap' }}>
                      <span className="tenu" style={{ fontFamily: 'var(--mono)', fontSize: 10.5 }}>
                        {o.valeurMarchande} or
                        {lot.quantite > 1 && ` · ${o.valeurMarchande * lot.quantite} en tout`}
                      </span>
                      {bloque ? (
                        <span className="tenu objet-bloque" title={bloque.phrase}>{bloque.court}</span>
                      ) : (
                        <button
                          className="discret"
                          style={{ marginLeft: 16 }}
                          onClick={() => void agir(() => api.equiper(id, o.id))}
                        >
                          porter
                        </button>
                      )}
                    </span>
                  </div>
                  {ouvert === o.id && (
                    <FicheObjet
                      o={o}
                      classe={p.classe}
                      niveau={p.niveau}
                      surPorter={(e) => {
                        setOuvert(null)
                        void agir(() => api.equiper(id, o.id, e as Emplacement))
                      }}
                      surConsommer={() => {
                        setOuvert(null)
                        void agir(() => api.consommer(id, o.id))
                      }}
                      surFermer={() => setOuvert(null)}
                    />
                  )}
                </div>
              )
            })}
          </div>
        )}
      </section>
    </div>
  )
}
